import * as yup from "yup";
import { toast } from "react-toastify";

import {
  AtStoreInfo,
  ByMailInfo,
  Contact,
  InPersonInfo,
  OnlineInfo,
  ProcedureDetail,
  ProcValidation,
  Reference,
  IssueFile,
  DescriptionFile,
  PlaceForProc,
  Belonging,
  ProcDocument,
  Destination,
  SmartApplyValidationResult,
} from "../core/types";
import { get } from "./get";
import { getMaxLength } from "./input-max-length";
import { set } from "./set";
import apiClient from "../../../apis/api-client";
import { reportProcManagementRelatedError } from "./error";
import { initializeProcedure } from "../core";

const errorMessage = {
  required: "必須入力項目です",
  maxLength: "最大入力文字数を超過しています",
  urlFormat: "URLに誤りがあります",
  countryFormat: "国コードに誤りがあります",
  postalFormat: "郵便番号に誤りがあります",
  telFormat: "番号に誤りがあります",
  emailFormat: "メールアドレスに誤りがあります",
  onlyEmptyString: "空白文字以外を入力してください",
  smartApplyAccessMustBeOpen:
    "パスコードなどによるアクセス制限が設定されているスマート申請を紐づけることはできません",
} as const;

export const MAX_FILE_SIZE_IN_PROC_MANAGEMENT = 5242880; // 5MB

export enum formatValidateType {
  url,
  countryCode,
  postalCode,
  tel,
  email,
}

function validateRequiredString(
  value: string,
  opt?: { maxLength?: number; format?: formatValidateType },
) {
  if (!yup.string().required().isValidSync(value)) {
    return errorMessage.required;
  }
  return validateNullableString(value, opt);
}

function validateNullableString(
  value?: string,
  opt?: { maxLength?: number; format?: formatValidateType },
) {
  if (!value || value === "" || !opt) {
    return "";
  }
  if (isOnlyEmptyString(value)) {
    return errorMessage.onlyEmptyString;
  }
  if (opt.maxLength && !yup.string().max(opt.maxLength).isValidSync(value)) {
    return errorMessage.maxLength;
  }
  if (opt.format !== undefined) {
    return validateFormat(value, opt.format);
  }
  return "";
}

function validateFormat(value: string, format: formatValidateType) {
  let msg = "";
  switch (format) {
    case formatValidateType.url:
      msg = yup.string().url().isValidSync(value) ? "" : errorMessage.urlFormat;
      break;
    case formatValidateType.countryCode:
      msg = yup
        .string()
        .matches(/^[0-9]{1,3}$/)
        .isValidSync(value)
        ? ""
        : errorMessage.countryFormat;
      break;
    case formatValidateType.postalCode:
      msg = yup
        .string()
        .matches(/^\d{7}$|^\d{3}-\d{4}$/)
        .isValidSync(value)
        ? ""
        : errorMessage.postalFormat;
      break;
    case formatValidateType.tel:
      msg = yup
        .string()
        .matches(/^0\d{9,10}$|^0\d{1,4}-\d{1,4}-\d{3,4}$/)
        .isValidSync(value)
        ? ""
        : errorMessage.telFormat;
      break;
    case formatValidateType.email:
      msg = yup.string().email().isValidSync(value)
        ? ""
        : errorMessage.emailFormat;
      break;
    default:
      break;
  }
  return msg;
}

export function validateProcedure(proc: ProcedureDetail) {
  let result: ProcValidation = { errorCounts: 0 };
  result = validateSummarySection(proc, result);
  result = validateRequirementSection(proc, result);
  result = validateSupportSection(proc, result);
  result = validateRelationSection(proc, result);
  if (proc.onlineInfo && proc.acceptAppOnline) {
    result = validateOnlineInfoSection(proc.onlineInfo, result);
  }
  if (proc.byMailInfo && proc.acceptAppByMail) {
    result = validateByMailInfoSection(proc.byMailInfo, result);
  }
  if (proc.inPersonInfo && proc.acceptAppInPerson) {
    result = validateInPersonInfoSection(proc.inPersonInfo, result);
  }
  if (proc.atStoreInfo && proc.acceptIssueAtStore) {
    result = validateAtStoreInfoSection(proc.atStoreInfo, result);
  }
  return result;
}

function validateSummarySection(proc: ProcedureDetail, result: ProcValidation) {
  const nameResult = validateRequiredString(proc.name, {
    maxLength: getMaxLength("procName"),
  });
  set(result, ["name"], nameResult);
  incrementErrorCountsIfError(result, nameResult);

  const titleResult = validateNullableString(proc.title, {
    maxLength: getMaxLength("procTitle"),
  });
  set(result, ["title"], titleResult);
  incrementErrorCountsIfError(result, titleResult);

  const summaryResult = validateRequiredString(proc.summary, {
    maxLength: getMaxLength("procSummary"),
  });
  set(result, ["summary"], summaryResult);
  incrementErrorCountsIfError(result, summaryResult);

  if (proc.procedureTags) {
    proc.procedureTags.forEach((t, i) => {
      const procTagResult = validateRequiredString(t.id);
      set(result, ["procedureTags", i], procTagResult);
      incrementErrorCountsIfError(result, procTagResult);
    });
  }

  return result;
}

function validateRequirementSection(
  proc: ProcedureDetail,
  result: ProcValidation,
) {
  const targetResult = validateNullableString(proc.target, {
    maxLength: getMaxLength("procTarget"),
  });
  set(result, ["target"], targetResult);
  incrementErrorCountsIfError(result, targetResult);

  const dtlRequirementResult = validateNullableString(
    proc.detailedRequirement,
    { maxLength: getMaxLength("procDtlRequirement") },
  );
  set(result, ["detailedRequirement"], dtlRequirementResult);
  incrementErrorCountsIfError(result, dtlRequirementResult);

  const triggerResult = validateNullableString(proc.trigger, {
    maxLength: getMaxLength("procTrigger"),
  });
  set(result, ["trigger"], triggerResult);
  incrementErrorCountsIfError(result, triggerResult);

  const deadlineResult = validateNullableString(proc.deadline, {
    maxLength: getMaxLength("procDeadline"),
  });
  set(result, ["deadline"], deadlineResult);
  incrementErrorCountsIfError(result, deadlineResult);

  return result;
}

function validateOnlineInfoSection(info: OnlineInfo, result: ProcValidation) {
  if (info.applicants) {
    info.applicants.forEach((a, i) => {
      const applicantResult = validateRequiredString(a);
      set(result, ["onlineInfo", "applicants", i], applicantResult);
      incrementErrorCountsIfError(result, applicantResult);
    });
  }

  if (info.grafferSmartApplyInfo) {
    const smartApplyResult = validateRequiredString(
      info.grafferSmartApplyInfo.procedureId,
    );
    set(result, ["onlineInfo", "grafferSmartApplyInfo"], smartApplyResult);
    incrementErrorCountsIfError(result, smartApplyResult);
  } else {
    const onlineUrlResult = validateRequiredString(info.applicationUrl ?? "", {
      format: formatValidateType.url,
    });
    set(result, ["onlineInfo", "applicationUrl"], onlineUrlResult);
    incrementErrorCountsIfError(result, onlineUrlResult);
  }

  const onlineNoticeResult = validateNullableString(info.notice, {
    maxLength: getMaxLength("onlineInfoNotice"),
  });
  set(result, ["onlineInfo", "notice"], onlineNoticeResult);
  incrementErrorCountsIfError(result, onlineNoticeResult);

  // TODO 最大長を決めてバリデーションを行う
  const onlineProxyResult = validateNullableString(info.acceptAppByProxyIf);
  set(result, ["onlineInfo", "acceptAppByProxyIf"], onlineProxyResult);
  incrementErrorCountsIfError(result, onlineProxyResult);

  // TODO 最大長を決めてバリデーションを行う
  const onlineFeeResult = validateNullableString(info.fee);
  set(result, ["onlineInfo", "fee"], onlineFeeResult);
  incrementErrorCountsIfError(result, onlineFeeResult);

  return result;
}

function validateByMailInfoSection(info: ByMailInfo, result: ProcValidation) {
  if (info.applicants) {
    info.applicants.forEach((a, i) => {
      const applicantResult = validateRequiredString(a);
      set(result, ["byMailInfo", "applicants", i], applicantResult);
      incrementErrorCountsIfError(result, applicantResult);
    });
  }
  if (info.documents) {
    result = validateDocuments(info.documents, result, [
      "byMailInfo",
      "documents",
    ]);
  }
  if (info.submissions) {
    result = validateBelongings(info.submissions, result, [
      "byMailInfo",
      "submissions",
    ]);
  }
  if (info.destinations) {
    result = validateDestination(info.destinations, result, [
      "byMailInfo",
      "destinations",
    ]);
  }

  // TODO 最大長を決めてバリデーションを行う
  const byMailProxyResult = validateNullableString(info.acceptAppByProxyIf);
  set(result, ["byMailInfo", "acceptAppByProxyIf"], byMailProxyResult);
  incrementErrorCountsIfError(result, byMailProxyResult);

  // TODO 最大長を決めてバリデーションを行う
  const byMailFeeResult = validateNullableString(info.fee);
  set(result, ["byMailInfo", "fee"], byMailFeeResult);
  incrementErrorCountsIfError(result, byMailFeeResult);

  return result;
}

function validateInPersonInfoSection(
  info: InPersonInfo,
  result: ProcValidation,
) {
  if (info.applicants) {
    info.applicants.forEach((a, i) => {
      const applicantResult = validateRequiredString(a);
      set(result, ["inPersonInfo", "applicants", i], applicantResult);
      incrementErrorCountsIfError(result, applicantResult);
    });
  }
  if (info.documents) {
    result = validateDocuments(info.documents, result, [
      "inPersonInfo",
      "documents",
    ]);
  }
  if (info.belongings) {
    result = validateBelongings(info.belongings, result, [
      "inPersonInfo",
      "belongings",
    ]);
  }
  if (info.places) {
    result = validatePlaces(info.places, result, ["inPersonInfo", "places"]);
  }

  // TODO 最大長を決めてバリデーションを行う
  const inPersonProxyResult = validateNullableString(info.acceptAppByProxyIf);
  set(result, ["inPersonInfo", "acceptAppByProxyIf"], inPersonProxyResult);
  incrementErrorCountsIfError(result, inPersonProxyResult);

  // TODO 最大長を決めてバリデーションを行う
  const inPersonFeeResult = validateNullableString(info.fee);
  set(result, ["inPersonInfo", "fee"], inPersonFeeResult);
  incrementErrorCountsIfError(result, inPersonFeeResult);

  return result;
}

function validateAtStoreInfoSection(info: AtStoreInfo, result: ProcValidation) {
  const feeResult = validateNullableString(info.fee, {
    maxLength: getMaxLength("atStoreFee"),
  });
  set(result, ["atStoreInfo", "fee"], feeResult);
  incrementErrorCountsIfError(result, feeResult);

  const noticeResult = validateNullableString(info.notice, {
    maxLength: getMaxLength("atStoreNotice"),
  });
  set(result, ["atStoreInfo", "notice"], noticeResult);
  incrementErrorCountsIfError(result, noticeResult);

  return result;
}

function validateSupportSection(proc: ProcedureDetail, result: ProcValidation) {
  const procDscResult = validateNullableString(proc.description, {
    maxLength: getMaxLength("procDescription"),
  });
  set(result, ["description"], procDscResult);
  incrementErrorCountsIfError(result, procDscResult);

  const procNoticeResult = validateNullableString(proc.notice, {
    maxLength: getMaxLength("procNotice"),
  });
  set(result, ["notice"], procNoticeResult);
  incrementErrorCountsIfError(result, procNoticeResult);

  if (proc.descriptionFiles) {
    result = validateDescriptionFiles(proc.descriptionFiles, result);
  }
  if (proc.issueFiles) {
    result = validateIssueFiles(proc.issueFiles, result);
  }
  if (proc.contacts) {
    result = validateContact(proc.contacts, result);
  }
  if (proc.references) {
    result = validateReference(proc.references, result);
  }

  return result;
}

function validateRelationSection(
  proc: ProcedureDetail,
  result: ProcValidation,
) {
  const grpNameResult = validateNullableString(proc.jurisdiction.groupName, {
    maxLength: getMaxLength("groupName"),
  });
  set(result, ["jurisdictionGrpName"], grpNameResult);
  incrementErrorCountsIfError(result, grpNameResult);

  return result;
}

function validateDestination(
  info: Destination[],
  result: ProcValidation,
  basePath: (string | number)[],
) {
  info.forEach((d, i) => {
    const dstCtrResult = validateRequiredString(d.countryCode, {
      format: formatValidateType.countryCode,
    });
    set(result, [...basePath, i, "countryCode"], dstCtrResult);
    incrementErrorCountsIfError(result, dstCtrResult);

    const dstPostalResult = validateRequiredString(d.postalCode, {
      format: formatValidateType.postalCode,
    });
    set(result, [...basePath, i, "postalCode"], dstPostalResult);
    incrementErrorCountsIfError(result, dstPostalResult);

    const destAdd1Result = validateRequiredString(d.address1, {
      maxLength: getMaxLength("byMailDestAddress"),
    });
    set(result, [...basePath, i, "address1"], destAdd1Result);
    incrementErrorCountsIfError(result, destAdd1Result);

    const destAdd2Result = validateRequiredString(d.address2, {
      maxLength: getMaxLength("byMailDestAddress"),
    });
    set(result, [...basePath, i, "address2"], destAdd2Result);
    incrementErrorCountsIfError(result, destAdd2Result);

    const destAdd3Result = validateNullableString(d.address3, {
      maxLength: getMaxLength("byMailDestAddress"),
    });
    set(result, [...basePath, i, "address3"], destAdd3Result);
    incrementErrorCountsIfError(result, destAdd3Result);

    const destNameResult = validateRequiredString(d.name, {
      maxLength: getMaxLength("byMailDestName"),
    });
    set(result, [...basePath, i, "name"], destNameResult);
    incrementErrorCountsIfError(result, destNameResult);

    const destTelResult = validateNullableString(d.tel, {
      format: formatValidateType.tel,
    });
    set(result, [...basePath, i, "tel"], destTelResult);
    incrementErrorCountsIfError(result, destTelResult);

    if (d.usedIf) {
      d.usedIf.forEach((u, j) => {
        // TODO 最大長を決めてバリデーションを行う
        const destUsedResult = validateNullableString(u);
        set(result, [...basePath, i, "usedIf", j], destUsedResult);
        incrementErrorCountsIfError(result, destUsedResult);
      });
    }
  });

  return result;
}

function validateDocuments(
  info: ProcDocument[],
  result: ProcValidation,
  basePath: (string | number)[],
) {
  info.forEach((d, i) => {
    const docNameResult = validateRequiredString(d.name, {
      maxLength: getMaxLength("documentName"),
    });
    set(result, [...basePath, i, "name"], docNameResult);
    incrementErrorCountsIfError(result, docNameResult);

    const fileResult = validateRequiredString(d.fileName);
    set(result, [...basePath, i, "file"], fileResult);
    incrementErrorCountsIfError(result, fileResult);

    const docDscResult = validateNullableString(d.description, {
      maxLength: getMaxLength("documentDescription"),
    });
    set(result, [...basePath, i, "description"], docDscResult);
    incrementErrorCountsIfError(result, docDscResult);

    const docUrlResult = validateNullableString(d.helpUrl, {
      format: formatValidateType.url,
    });
    set(result, [...basePath, i, "helpUrl"], docUrlResult);
    incrementErrorCountsIfError(result, docUrlResult);

    if (d.requireIf) {
      d.requireIf.forEach((r, j) => {
        // TODO 最大長を決めてバリデーションを行う
        const docReqResult = validateNullableString(r);
        set(result, [...basePath, i, "requireIf", j], docReqResult);
        incrementErrorCountsIfError(result, docReqResult);
      });
    }
  });

  return result;
}

function validateBelongings(
  info: Belonging[],
  result: ProcValidation,
  basePath: (string | number)[],
) {
  info.forEach((s, i) => {
    const docNameResult = validateRequiredString(s.name, {
      maxLength: getMaxLength("submissionName"),
    });
    set(result, [...basePath, i, "name"], docNameResult);
    incrementErrorCountsIfError(result, docNameResult);

    const docDscResult = validateNullableString(s.description, {
      maxLength: getMaxLength("submissionDescription"),
    });
    set(result, [...basePath, i, "description"], docDscResult);
    incrementErrorCountsIfError(result, docDscResult);

    const docUrlResult = validateNullableString(s.helpUrl, {
      format: formatValidateType.url,
    });
    set(result, [...basePath, i, "helpUrl"], docUrlResult);
    incrementErrorCountsIfError(result, docUrlResult);

    if (s.requireIf) {
      s.requireIf.forEach((r, j) => {
        // TODO 最大長を決めてバリデーションを行う
        const docReqResult = validateNullableString(r);
        set(result, [...basePath, i, "requireIf", j], docReqResult);
        incrementErrorCountsIfError(result, docReqResult);
      });
    }
  });

  return result;
}

function validatePlaces(
  info: PlaceForProc[],
  result: ProcValidation,
  basePath: (string | number)[],
) {
  info.forEach((p, i) => {
    const placeIdResult = validateRequiredString(p.id ?? "");
    set(result, [...basePath, i, "id"], placeIdResult);
    incrementErrorCountsIfError(result, placeIdResult);

    const placeCtrResult = validateNullableString(p.counterName, {
      maxLength: getMaxLength("placeCounterName"),
    });
    set(result, [...basePath, i, "counterName"], placeCtrResult);
    incrementErrorCountsIfError(result, placeCtrResult);

    if (p.usedIf) {
      p.usedIf.forEach((u, j) => {
        // TODO 最大長を決めてバリデーションを行う
        const placeUsedResult = validateNullableString(u);
        set(result, [...basePath, i, "usedIf", j], placeUsedResult);
        incrementErrorCountsIfError(result, placeUsedResult);
      });
    }
  });

  return result;
}

function validateDescriptionFiles(
  info: DescriptionFile[],
  result: ProcValidation,
) {
  info.forEach((f, i) => {
    const fileNameResult = validateRequiredString(f.name, {
      maxLength: getMaxLength("descriptionFileName"),
    });
    set(result, ["descriptionFiles", i, "name"], fileNameResult);
    incrementErrorCountsIfError(result, fileNameResult);

    const fileResult = validateRequiredString(f.fileName);
    set(result, ["descriptionFiles", i, "file"], fileResult);
    incrementErrorCountsIfError(result, fileResult);
  });

  return result;
}

function validateIssueFiles(info: IssueFile[], result: ProcValidation) {
  info.forEach((f, i) => {
    const fileNameResult = validateRequiredString(f.name, {
      maxLength: getMaxLength("issueFilesName"),
    });
    set(result, ["issueFiles", i, "name"], fileNameResult);
    incrementErrorCountsIfError(result, fileNameResult);

    const dscResult = validateNullableString(f.description, {
      maxLength: getMaxLength("issueFilesDescription"),
    });
    set(result, ["issueFiles", i, "description"], dscResult);
    incrementErrorCountsIfError(result, dscResult);

    const noticeResult = validateNullableString(f.notice, {
      maxLength: getMaxLength("issueFilesNotice"),
    });
    set(result, ["issueFiles", i, "notice"], noticeResult);
    incrementErrorCountsIfError(result, noticeResult);
  });

  return result;
}

function validateContact(info: Contact[], result: ProcValidation) {
  info.forEach((c, i) => {
    const contNameResult = validateRequiredString(c.name, {
      maxLength: getMaxLength("contactName"),
    });
    set(result, ["contacts", i, "name"], contNameResult);
    incrementErrorCountsIfError(result, contNameResult);

    const contTelResult = validateRequiredString(c.tel, {
      format: formatValidateType.tel,
    });
    set(result, ["contacts", i, "tel"], contTelResult);
    incrementErrorCountsIfError(result, contTelResult);

    const contFaxResult = validateNullableString(c.fax, {
      format: formatValidateType.tel,
    });
    set(result, ["contacts", i, "fax"], contFaxResult);
    incrementErrorCountsIfError(result, contFaxResult);

    const contEmailResult = validateNullableString(c.email, {
      format: formatValidateType.email,
    });
    set(result, ["contacts", i, "email"], contEmailResult);
    incrementErrorCountsIfError(result, contEmailResult);

    if (c.usedIf) {
      c.usedIf.forEach((u, j) => {
        // TODO 最大長を決めてバリデーションを行う
        const contUsedIfResult = validateNullableString(u);
        set(result, ["contacts", i, "usedIf", j], contUsedIfResult);
        incrementErrorCountsIfError(result, contUsedIfResult);
      });
    }
  });

  return result;
}

function validateReference(info: Reference[], result: ProcValidation) {
  info.forEach((r, i) => {
    const refNameResult = validateRequiredString(r.name, {
      maxLength: getMaxLength("referenceName"),
    });
    set(result, ["references", i, "name"], refNameResult);
    incrementErrorCountsIfError(result, refNameResult);

    const refUrlResult = validateRequiredString(r.url, {
      format: formatValidateType.url,
    });
    set(result, ["references", i, "url"], refUrlResult);
    incrementErrorCountsIfError(result, refUrlResult);
  });

  return result;
}

function incrementErrorCountsIfError(
  result: ProcValidation,
  individualResult: string,
) {
  if (individualResult === "") {
    return;
  }
  const prevCounts = get(result, ["errorCounts"]);
  const newCounts = prevCounts + 1;
  set(result, ["errorCounts"], newCounts);
}

export const validateSmartApply = async (
  procedure: ProcedureDetail,
): Promise<SmartApplyValidationResult> => {
  const smartApplyProcedureId =
    procedure.onlineInfo?.grafferSmartApplyInfo?.procedureId;
  if (
    typeof smartApplyProcedureId !== "string" ||
    smartApplyProcedureId.length === 0
  ) {
    return { error: undefined };
  }

  return {
    error: await validateSmartApplyAccessIsOpen(smartApplyProcedureId),
  };
};

const validateSmartApplyAccessIsOpen = async (
  smartApplyProcedureId: string,
): Promise<(typeof errorMessage)[keyof typeof errorMessage] | undefined> => {
  try {
    const { data } = await apiClient.fetchProcedure(smartApplyProcedureId);
    if (data.isFormAccessRestricted()) {
      return errorMessage.smartApplyAccessMustBeOpen;
    }
  } catch (error) {
    reportProcManagementRelatedError(error);
    toast.error("選択しているスマート申請データを取得できませんでした");
  }

  return undefined;
};

export const updateAsyncValidationTarget = (
  path: (string | number)[],
  value: any,
): boolean => {
  const p = initializeProcedure();
  set(p, path, value);
  return hasAsyncValidationTarget(p);
};

const hasAsyncValidationTarget = (proc: ProcedureDetail): boolean => {
  return (
    typeof proc.onlineInfo?.grafferSmartApplyInfo?.procedureId === "string"
  );
};

export const isValidProcManagementFileSize = (file: File): boolean => {
  return file.size < MAX_FILE_SIZE_IN_PROC_MANAGEMENT;
};

const isOnlyEmptyString = (value: string): boolean => {
  return value.length > 0 && value.trim().length === 0;
};
